import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-all duration-200 cursor-pointer"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
              {article.category}
            </span>
            {article.isHot && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                热门
              </span>
            )}
          </div>
          <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200/70 last:border-0 cursor-pointer"
      >
        <div className="w-20 h-14 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-400">
            <span>{article.publishDate}</span>
            <span>{article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万阅读` : `${article.readCount}阅读`}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg bg-background-50 hover:bg-background-100 transition-all duration-200 overflow-hidden cursor-pointer"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800 font-medium backdrop-blur-sm whitespace-nowrap">
            {article.category}
          </span>
          {article.isHot && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500 text-background-50 font-medium whitespace-nowrap">
              热门
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.publishDate}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}